// Reads and writes the timesheet entry form for the voice input
// (timesheet_voice_controller). Every helper takes a scope element and a name
// prefix, so the same code serves the form's own fields
// (configurable_time_sheet / custom_fields / allowances), a Mehrbaustellenbuchung
// row (site_allocations[N]) and a further Mehrfacheintrag block
// (additional_entries[N]).
//
// Values are written onto the NAMED input and announced with input + change:
// every time and pause widget (time-picker, time-stepper, break steppers,
// duration and minutes inputs) keeps its named hidden field as the source of
// truth and follows a change on it.

export function notify(input) {
  input.dispatchEvent(new Event("input", { bubbles: true }))
  input.dispatchEvent(new Event("change", { bubbles: true }))
}

export function namedInput(scope, name) {
  const candidates = scope.querySelectorAll(`[name="${name}"]`)
  return (
    Array.from(candidates).find((input) => input.type !== "hidden" || isValueField(input)) ||
    candidates[0] ||
    null
  )
}

// A hidden input that IS the field (a widget's value) rather than Rails' unchecked
// checkbox companion, which shares the name and must never be written. The checkbox
// is not always its next sibling: the Zulage switch wraps it in a <span>.
function isValueField(input) {
  const scope = input.form || input.ownerDocument
  return !scope.querySelector(`input[type="checkbox"][name="${input.name}"]`)
}

// Writes a scalar ("HH:MM", decimal string, text). Returns whether the field exists.
export function writeScalar(scope, name, value) {
  const input = namedInput(scope, name)
  if (!input) return false
  input.value = value ?? ""
  notify(input)
  return true
}

export function readScalar(scope, name) {
  const input = namedInput(scope, name)
  return input && input.value !== "" ? input.value : null
}

// ---- custom fields -------------------------------------------------------------

// prefix: "custom_fields" | "site_allocations[3][custom_fields]" | …
// setSelect(select, value|values) goes through SlimSelect.
export function applyCustomFields(scope, prefix, values, setSelect) {
  const missing = []
  Object.entries(values || {}).forEach(([key, value]) => {
    const multi = scope.querySelector(`select[name="${prefix}[${key}][]"]`)
    if (multi && Array.isArray(value)) {
      setSelect(
        multi,
        value.map((entry) => String(entry)),
      )
      return
    }
    const input = namedInput(scope, `${prefix}[${key}]`)
    if (!input) {
      missing.push(key)
      return
    }
    if (input.type === "checkbox") {
      input.checked = value === "1" || value === true || value === "true"
      notify(input)
    } else if (input.tagName === "SELECT") {
      setSelect(input, String(value))
    } else {
      input.value = Array.isArray(value) ? value.join(", ") : value
      notify(input)
    }
  })
  return missing
}

export function readCustomFields(scope, prefix) {
  const values = {}
  const pattern = new RegExp(`^${escapeRegExp(prefix)}\\[([^\\]]+)\\]`)
  scope.querySelectorAll(`[name^="${prefix}["]`).forEach((input) => {
    const match = input.name.match(pattern)
    if (!match || (input.type === "hidden" && !isWidgetValue(input))) return
    const key = match[1]
    if (input.type === "checkbox") {
      values[key] = input.checked ? "1" : "0"
    } else if (input.name.endsWith("[]")) {
      if (!Array.isArray(values[key])) values[key] = []
      Array.from(input.selectedOptions || []).forEach((option) => values[key].push(option.value))
    } else if (input.value !== "") {
      values[key] = input.value
    }
  })
  return values
}

// Time/duration custom fields keep their value in a hidden widget field.
function isWidgetValue(input) {
  return Object.keys(input.dataset).some((key) => /Target$/.test(key)) && isValueField(input)
}

// ---- allowances -------------------------------------------------------------------

// prefix: "allowances" | "site_allocations[3][allowances]" | …
export function applyAllowances(scope, prefix, values) {
  const missing = []
  Object.entries(values || {}).forEach(([key, data]) => {
    const input = namedInput(scope, `${prefix}[${key}][input]`)
    if (!input) {
      missing.push(key)
      return
    }
    if (input.type === "checkbox") {
      input.checked = data.input === "1" || data.input === true || data.input === "true"
    } else {
      input.value = data.input
    }
    notify(input)
    if (data.whole_day !== undefined) {
      const wholeDay = namedInput(scope, `${prefix}[${key}][whole_day]`)
      if (wholeDay && wholeDay.type === "checkbox") {
        wholeDay.checked = data.whole_day === true || data.whole_day === "true"
        notify(wholeDay)
      }
    }
  })
  return missing
}

// whole_day travels with the input: the server compares the whole allowance
// hash against its suggestion, so a draft that omits the flag never matches
// and every filled allowance would come back as "changed" — re-applying the
// suggestion's whole_day and unticking what the user set by hand.
export function readAllowances(scope, prefix) {
  const values = {}
  const pattern = new RegExp(`^${escapeRegExp(prefix)}\\[([^\\]]+)\\]\\[input\\]$`)
  scope.querySelectorAll(`[name^="${prefix}["][name$="[input]"]`).forEach((input) => {
    const match = input.name.match(pattern)
    if (!match || (input.type === "hidden" && !isValueField(input))) return
    const value = input.type === "checkbox" ? (input.checked ? "1" : "0") : input.value
    if (value === "") return

    const wholeDay = namedInput(scope, `${prefix}[${match[1]}][whole_day]`)
    values[match[1]] = {
      input: value,
      whole_day: Boolean(wholeDay && wholeDay.type === "checkbox" && wholeDay.checked),
    }
  })
  return values
}

// ---- break periods (von/bis rows) ----------------------------------------------------

// The nested-form block holding the von/bis rows of a scope, or null when the
// template collects the pause as one total.
export function breakPeriodsContainer(scope) {
  return (
    Array.from(scope.querySelectorAll("[data-controller~='nested-form']")).find((element) =>
      element
        .querySelector("template[data-nested-form-target='template']")
        ?.innerHTML.includes("break_from"),
    ) || null
  )
}

function visibleRows(container) {
  return Array.from(container.querySelectorAll("[data-break-period-row]")).filter(
    (row) => row.style.display !== "none",
  )
}

export function readBreakPeriods(scope) {
  const container = breakPeriodsContainer(scope)
  if (!container) return null
  const periods = visibleRows(container)
    .map((row) => ({
      break_from: row.querySelector("[data-break-from]")?.value || "",
      break_to: row.querySelector("[data-break-to]")?.value || "",
    }))
    .filter((period) => period.break_from && period.break_to)
  return periods.length ? periods : null
}

// Replaces the employee's own rows with +periods+. Rows fixed from the
// work-time model (data-break-period-predefined) stay, and a spoken period that
// repeats one of them is not added twice. Returns false when the scope has no
// von/bis rows at all.
export function applyBreakPeriods(scope, periods, nestedForm) {
  const container = breakPeriodsContainer(scope)
  if (!container || !nestedForm) return false

  const fixed = visibleRows(container).filter((row) =>
    row.hasAttribute("data-break-period-predefined"),
  )
  const fixedKeys = fixed.map((row) => periodKey(row))
  visibleRows(container)
    .filter((row) => !fixed.includes(row))
    .forEach((row) => {
      const remove = row.querySelector("[data-action*='remove_association']")
      if (remove) nestedForm.remove_association({ preventDefault() {}, target: remove })
    })

  ;(periods || []).forEach((period) => {
    if (fixedKeys.includes(`${period.break_from}-${period.break_to}`)) return
    nestedForm.add_association({ preventDefault() {} })
    const rows = visibleRows(container)
    const row = rows[rows.length - 1]
    if (!row) return
    writeHidden(row.querySelector("[data-break-from]"), period.break_from)
    writeHidden(row.querySelector("[data-break-to]"), period.break_to)
  })
  return true
}

function periodKey(row) {
  return `${row.querySelector("[data-break-from]")?.value}-${row.querySelector("[data-break-to]")?.value}`
}

function writeHidden(input, value) {
  if (!input) return
  input.value = value
  notify(input)
}

// ---- small helpers ----------------------------------------------------------------------

export function minutesOf(hhmm) {
  const match = /^(\d{1,2}):(\d{2})$/.exec(hhmm || "")
  return match ? Number(match[1]) * 60 + Number(match[2]) : null
}

export function hhmm(minutes) {
  const clamped = Math.max(0, Math.min(minutes, 23 * 60 + 59))
  return `${String(Math.floor(clamped / 60)).padStart(2, "0")}:${String(clamped % 60).padStart(2, "0")}`
}

function escapeRegExp(text) {
  return text.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")
}
